import { SearchCheck, Split, MousePointerClick, Store, LineChart, HandCoins, ArrowLeftRight } from "lucide-react";

const STEPS = [
  { icon: SearchCheck, title: "Rigorous pre-vetting by frantiger", text: "Every franchise unit is underwritten on unit economics, brand strength, promoter track record and location before it can list. Only opportunities that clear our diligence make the cut." },
  { icon: Split, title: "Capital is structured 60:40", text: "The operator commits 60% of the outlet's capital as skin-in-the-game; the remaining 40% is opened to investors — divided into fractions from ₹5,000, capped at a pool of 200 verified individuals." },
  { icon: MousePointerClick, title: "You subscribe via CCDs / NCDs", text: "Your fraction is issued as a private debt instrument — Compulsorily Convertible or Non-Convertible Debentures — collateralised against the outlet. It behaves like structured P2P lending, backed by a real, operating franchise." },
  { icon: Store, title: "Professional operators run the store", text: "Certified master-franchise operators handle setup, staffing, inventory and daily operations while you stay fully passive." },
  { icon: LineChart, title: "Earn coupons & exit with liquidity", text: "Each unit carries a unique 6-letter scrip ID (e.g. MCDHSR). Track sales, receive monthly coupon payouts, and exit early via Offer to Sell — or scale up via Offer to Buy on the secondary board." },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="bg-[#eef2f7] py-24">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid items-start gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="lg:sticky lg:top-28">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">What is fractional franchise funding?</p>
            <h2 className="font-heading mt-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Many investors. One outlet. Shared cash flows.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Fractional franchise funding lets you participate in a pre-vetted franchise outlet
              through fractions — instead of underwriting the whole unit alone. It is structured like
              peer-to-peer private lending: your fraction is issued against{" "}
              <span className="font-bold text-slate-800">CCDs / NCDs</span>, every opportunity is{" "}
              <span className="font-bold text-slate-800">pre-vetted by frantiger</span>, and each unit
              carries a <span className="font-bold text-slate-800">unique 6-letter scrip ID</span>. A
              maximum of <span className="font-bold text-orange-600">200 investors</span> can subscribe
              to any single pool.
            </p>
            <div className="neu-inset mt-8 rounded-3xl p-6" data-testid="fraction-example-card">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">A simple example</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                A cafe expansion needs <span className="font-bold text-slate-900">₹1,12,50,000</span>.
                The operator commits <span className="font-bold text-slate-900">60% (₹67,50,000)</span>,
                and the <span className="font-bold text-orange-600">40% pool (₹45,00,000)</span> is
                raised from up to <span className="font-bold text-slate-900">200 investors</span> in
                fractions from <span className="font-bold text-orange-600">₹5,000</span> — all sharing
                the outlet's coupons proportionally.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["₹5K", "₹10K", "₹25K", "₹5K", "₹50K", "₹5K"].map((t, i) => (
                  <span key={i} className="neu-badge rounded-full px-3 py-1 text-xs font-bold text-slate-600">{t}</span>
                ))}
                <span className="rounded-full bg-orange-500 px-3 py-1 text-xs font-bold text-white">= 40% pool funded</span>
              </div>
            </div>
          </div>

          <ol className="space-y-5">
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <li key={title} className="neu-card flex gap-5 rounded-3xl p-6" data-testid={`how-step-${i + 1}`}>
                <div className="flex flex-col items-center gap-2">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/40">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-heading text-xs font-black text-slate-400">0{i + 1}</span>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="liquidity-block">
          <div className="neu-card rounded-3xl p-7" data-testid="offer-to-buy-card">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/40">
                <HandCoins className="h-5 w-5" />
              </span>
              <h3 className="font-heading text-lg font-bold text-slate-900">Offer to Buy</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Want a larger position? Browse fractions of any listed scrip that existing investors
              have put up for sale, and subscribe in a few clicks — a flat{" "}
              <span className="font-bold text-slate-800">₹100 platform fee</span> applies per trade.
            </p>
          </div>
          <div className="neu-card rounded-3xl p-7" data-testid="offer-to-sell-card">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/40">
                <ArrowLeftRight className="h-5 w-5" />
              </span>
              <h3 className="font-heading text-lg font-bold text-slate-900">Offer to Sell</h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Need to exit before maturity? List your fraction on the secondary board for other
              pool members to buy — genuine liquidity that traditional franchise investments simply
              don't offer. A flat <span className="font-bold text-slate-800">₹100 platform fee</span>{" "}
              applies per trade.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
